import React, { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  defaultRoutesByRole,
  isSharedPublicPath,
  paths,
  roles,
} from './paths';
import { routesByRole } from './routes';
import { useAppRole } from './useRolePath';
import type { AppRoute } from './types';

function isAuthPath(pathname: string) {
  return (
    pathname.startsWith('/auth') ||
    pathname.includes('sign-in') ||
    pathname.includes('sign-up')
  );
}

const RoutesByRole = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useAppRole();

  useEffect(() => {
    if (userRole !== roles.NONE) {
      if (isAuthPath(location.pathname)) {
        navigate(defaultRoutesByRole[userRole], { replace: true });
        return;
      }
      if (isSharedPublicPath(location.pathname)) return;
      if (location.pathname.startsWith(`/${userRole}`)) {
        // Legacy role-prefixed shared pages → canonical bare URL
        for (const shared of [
          paths.aboutUs,
          paths.meet,
          paths.changelog,
          paths.publicOffer,
        ]) {
          const legacyPrefixed = `/${userRole}${shared}`;
          if (
            location.pathname === legacyPrefixed ||
            location.pathname.startsWith(`${legacyPrefixed}/`)
          ) {
            navigate(`${shared}${location.hash}`, { replace: true });
            return;
          }
        }
        return;
      }
      return;
    }

    if (location.pathname.includes('/sign-up')) return;
    if (location.pathname.startsWith('/auth')) return;
    if (isSharedPublicPath(location.pathname)) return;
    if (location.pathname.startsWith('/validation')) return;
    if (location.pathname === '/') return;

    navigate(paths.signIn, { replace: true });
  }, [userRole, location.pathname, location.hash, navigate]);

  const table = routesByRole[userRole] || routesByRole.none;

  return (
    <Routes>
      {table.map((route: AppRoute) => (
        <Route key={route.route} path={route.route} element={route.element} />
      ))}
    </Routes>
  );
};

export default RoutesByRole;
